class BlogFilter {
  constructor(root) {
    this.root = root;
    this.content = root.querySelector('[data-blog-filter-content]');
    this.toggle = root.querySelector('[data-blog-filter-toggle]');
    this.drawer = root.querySelector('[data-blog-filter-drawer]');
    this.toggleText = root.querySelector('.blog-filter__toggle-text');
    this.heading = root.querySelector('.blog-filter__heading');
    this.defaultToggleLabel = this.heading ? this.heading.textContent.trim() : 'BLOG';
    this.requestId = 0;

    this.onFilterClick = this.onFilterClick.bind(this);
    this.onContentClick = this.onContentClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    root.addEventListener('click', this.onFilterClick);
    if (this.content) this.content.addEventListener('click', this.onContentClick);
    window.addEventListener('popstate', this.onPopState);

    if (this.toggle && this.drawer) {
      this.closeButtons = root.querySelectorAll('[data-blog-filter-close]');
      this.toggle.addEventListener('click', this.onToggle);
      this.closeButtons.forEach((btn) => btn.addEventListener('click', this.onClose));
    }
  }

  onFilterClick(event) {
    const link = event.target.closest('[data-blog-filter-link]');
    if (!link || !this.root.contains(link)) return;
    event.preventDefault();
    this.load(link.href, { push: true, activeLink: link });
    this.close();
  }

  onContentClick(event) {
    const link = event.target.closest('.pagination a, .pagination__item');
    if (!link || !this.content.contains(link)) return;
    const href = link.getAttribute('href');
    if (!href || href === '#') return;
    event.preventDefault();
    this.load(href, { push: true });
  }

  onPopState() {
    this.load(window.location.href, { push: false });
  }

  async load(url, { push = false, activeLink = null } = {}) {
    if (!this.content) return;

    const requestId = ++this.requestId;
    this.content.classList.add('is-loading');

    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Failed to load blog articles');

      const html = await response.text();
      if (requestId !== this.requestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextContent = doc.querySelector('[data-blog-filter-content]');
      if (!nextContent) throw new Error('Blog content not found');

      this.content.innerHTML = nextContent.innerHTML;
      this.setActiveLink(activeLink, url);
      if (push) history.pushState({}, '', url);

      this.scrollToTop();
    } catch (error) {
      window.location.href = url;
    } finally {
      if (requestId === this.requestId) {
        this.content.classList.remove('is-loading');
      }
    }
  }

  scrollToTop() {
    const headerOffset =
      parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) ||
      document.querySelector('sticky-header')?.offsetHeight ||
      document.querySelector('.header-wrapper')?.offsetHeight ||
      0;
    const top =
      this.root.getBoundingClientRect().top + window.pageYOffset - headerOffset - 12;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }

  setActiveLink(activeLink, url) {
    const links = this.root.querySelectorAll('[data-blog-filter-link]');
    let matched = activeLink;

    if (!matched) {
      const targetPath = new URL(url, window.location.origin).pathname.replace(/\/$/, '');
      matched = Array.from(links).find((link) => {
        const linkPath = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
        return linkPath === targetPath;
      });
    }

    links.forEach((link) => {
      const isActive = matched ? link === matched : false;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (this.toggleText) {
      this.toggleText.textContent =
        matched && matched.textContent.trim()
          ? matched.textContent.trim()
          : this.defaultToggleLabel;
    }
  }

  onToggle() {
    if (this.drawer.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.drawer || !this.toggle) return;
    this.drawer.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('blog-filter-drawer-open');
    document.addEventListener('keydown', this.onKeydown);
  }

  close() {
    if (!this.drawer || !this.toggle) return;
    this.drawer.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('blog-filter-drawer-open');
    document.removeEventListener('keydown', this.onKeydown);
  }

  onClose() {
    this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }
}

document.querySelectorAll('.main-blog--with-filter').forEach((root) => {
  new BlogFilter(root);
});
